import { cloneDeep } from 'lodash';
import { derived, get, writable } from 'svelte/store';
import { PageTypeEnum } from '#pagebuilder/lib/enums/PageTypeEnum.js';
import { userStore } from '#stores/user/user.js';
import { activeRoute } from '#stores/routes/routes.js';
import { getPersistSyncKey } from '#pagebuilder/lib/helpers/persistSelection.js';

export function synchronisablePageTypeStore(defaultValue, synchronisableMapCallback = null, allowSync = true) {
    const { subscribe, update } = writable(
        synchronisableMapCallback ? synchronisableMapCallback(defaultValue) : getDefaultSynchronisableMap(defaultValue),
    );

    const setValueForSyncKey = (syncKey, value) => {
        update((storeValue) => {
            storeValue[syncKey] = value;
            return storeValue;
        });
    };

    const setValue = (value) => {
        setValueForSyncKey(getSyncKey(allowSync), value);
    };

    const deletePageType = (syncKey) => {
        update((storeValue) => {
            delete storeValue[syncKey];
            return storeValue;
        });
    };

    return {
        subscribe,
        set: setValue,
        setValueForSyncKey,
        deletePageType,
        update,
    };
}

export function synchronisablePageTypeObjectStore(defaultValue = {}, synchronisableMapCallback = null, allowSync = true) {
    const { subscribe, set, setValueForSyncKey, deletePageType, update } = synchronisablePageTypeStore(defaultValue, synchronisableMapCallback, allowSync);

    const setKeyValueForSyncKey = (syncKey, key, value) => {
        update((storeValue) => {
            storeValue[syncKey][key] = value;
            return storeValue;
        });
    };

    const deleteKeyForSyncKey = (syncKey, key) => {
        update((storeValue) => {
            delete storeValue[syncKey][key];
            return storeValue;
        });
    };

    const setKeyValue = (key, value) => {
        setKeyValueForSyncKey(getSyncKey(allowSync), key, value);
    };

    const deleteKey = (key) => {
        deleteKeyForSyncKey(getSyncKey(allowSync), key);
    };

    return {
        subscribe,
        set,
        setValueForSyncKey,
        setKeyValue,
        setKeyValueForSyncKey,
        deleteKeyForSyncKey,
        deleteKey,
        deletePageType,
        update,
    };
}

export function derivedSynchronisableStore(synchronisableStore, allowSync = true) {
    return derived([userStore, synchronisableStore], ($storeValues) => {
        const [$userStore, $synchronisableStore] = $storeValues;

        return allowSync && $userStore?.sync_filter ? $synchronisableStore.sync : $synchronisableStore[getSyncKey(allowSync)];
    });
}

function getSyncKey(allowSync = true) {
    const route = get(activeRoute);
    if(route === null){
        // Router is not setup yet
        return 'sync';
    }
    const user = get(userStore);
    const syncKey = getPersistSyncKey(route);
    return allowSync && user.sync_filter ? 'sync' : syncKey;
}

export function getDefaultSynchronisableMap(defaultValue) {
    const defaultsMap = {
        sync: cloneDeep(defaultValue),
    };
    PageTypeEnum.cases
        .filter(value => value.includes('analytics.'))
        .forEach(caseValue => {
            defaultsMap[caseValue] = cloneDeep(defaultValue);
        });

    return defaultsMap;
}
